import extenso from "extenso";

import type { ClinicFieldKey, ResolvableDocumentField } from "@/api/db/connection";
import { CustomFieldValues } from "@/api/document/custom-field-values";
import type { DocumentPatternItem, DocumentPatternValueMap } from "@/api/document/patterns";
import { djs } from "@/lib/djs";

interface ResolverInput {
	fields: Map<number, ResolvableDocumentField>;
	values: Map<number, string>;
	patterns: DocumentPatternValueMap;
	profile: Map<ClinicFieldKey, string>;
	referenced: Set<number>;
	mode: "preview" | "resolved";
}

function dayPeriod(value: string) {
	const hour = Number(value.slice(0, 2));

	if (hour < 6) {
		return "da madrugada";
	}

	if (hour < 12) {
		return "da manhã";
	}

	if (hour < 18) {
		return "da tarde";
	}

	return "da noite";
}

export class DocumentValueResolver {
	constructor(private input: ResolverInput) {}

	globalValues() {
		const values = new Map(this.input.values);

		for (const field of this.input.fields.values()) {
			if (
				field.pattern_id !== null ||
				field.column !== "custom" ||
				!this.input.referenced.has(field.id)
			) {
				continue;
			}

			const value = this.canonical(field.id, null, new Set());

			if (value !== null) {
				values.set(field.id, value);
			}
		}

		return values;
	}

	patternValues() {
		const patterns: DocumentPatternValueMap = new Map();

		for (const [patternId, items] of this.input.patterns) {
			patterns.set(
				patternId,
				items.map((item) => {
					const values = new Map(item.values);

					for (const field of this.input.fields.values()) {
						if (
							field.pattern_id !== patternId ||
							field.source !== "derived" ||
							!this.input.referenced.has(field.id)
						) {
							continue;
						}

						const value = this.canonical(field.id, item, new Set());

						if (value !== null) {
							values.set(field.id, value);
						}
					}

					return { id: item.id, values };
				}),
			);
		}

		return patterns;
	}

	formatted(fieldId: number, item: DocumentPatternItem | null = null) {
		const field = this.field(fieldId);
		const value = this.canonical(fieldId, item, new Set());

		if (value === null) {
			return null;
		}

		return CustomFieldValues.format(field.input_type, value);
	}

	private canonical(
		fieldId: number,
		item: DocumentPatternItem | null,
		stack: Set<string>,
	): string | null {
		const field = this.field(fieldId);
		const scope = item === null ? "global" : item.id;
		const key = `${scope}:${fieldId}`;

		if (stack.has(key)) {
			throw new Error(`derived_field_cycle:${fieldId}`);
		}

		stack.add(key);
		const value = this.sourceValue(field, item, stack);
		stack.delete(key);

		if (value === null && this.input.mode === "resolved") {
			throw new Error(`unresolved_document_field:${fieldId}`);
		}

		return value;
	}

	private sourceValue(
		field: ResolvableDocumentField,
		item: DocumentPatternItem | null,
		stack: Set<string>,
	): string | null {
		switch (field.source) {
			case "patient":
			case "system":
				return null;
			case "input": {
				const value = item?.values.get(field.id) ?? this.input.values.get(field.id);

				if (value === undefined) {
					return null;
				}

				return value;
			}
			case "clinic":
				return this.clinicValue(field);
			case "policy":
				if (field.policy_value === undefined) {
					return null;
				}

				return field.policy_value;
			case "derived":
				return this.derivedValue(field, item, stack);
		}
	}

	private clinicValue(field: ResolvableDocumentField): string | null {
		if (field.clinic_key === null) {
			throw new Error(`clinic_field_without_key:${field.id}`);
		}

		const raw = this.input.profile.get(field.clinic_key);

		if (raw === undefined) {
			return null;
		}

		return CustomFieldValues.canonical(field.input_type, raw);
	}

	private derivedValue(
		field: ResolvableDocumentField,
		item: DocumentPatternItem | null,
		stack: Set<string>,
	): string | null {
		if (field.derived_operation === null || field.operand_field_id === null) {
			throw new Error(`derived_field_without_expression:${field.id}`);
		}

		const operand = this.field(field.operand_field_id);

		switch (field.derived_operation) {
			case "weekday": {
				const value = this.canonical(operand.id, item, stack);

				if (value === null) {
					return null;
				}

				return djs(value, "YYYY-MM-DD", true).locale("pt-br").format("dddd");
			}
			case "day_period": {
				const value = this.canonical(operand.id, item, stack);

				if (value === null) {
					return null;
				}

				return dayPeriod(value);
			}
			case "money_words": {
				const value = this.canonical(operand.id, item, stack);

				if (value === null) {
					return null;
				}

				return this.moneyWords(value);
			}
			case "sum": {
				if (operand.pattern_id === null || item !== null) {
					throw new Error(`invalid_sum_scope:${field.id}`);
				}

				let total = 0n;

				for (const patternItem of this.input.patterns.get(operand.pattern_id) ?? []) {
					const value = this.canonical(operand.id, patternItem, stack);

					if (value === null) {
						return null;
					}

					total += BigInt(value);
				}

				return String(total);
			}
		}
	}

	private field(id: number) {
		const field = this.input.fields.get(id);

		if (field === undefined) {
			throw new Error(`unknown_document_field:${id}`);
		}

		return field;
	}

	private moneyWords(canonical: string) {
		const padded = canonical.padStart(3, "0");
		const value = `${padded.slice(0, -2)},${padded.slice(-2)}`;

		return extenso(value, { mode: "currency", locale: "br", currency: { type: "BRL" } });
	}
}
